import { addressValidation, zipValid, type ReportValidation } from './address-validation.ts';
import { chemicalCarrierLabel } from './chemical-carriers.ts';
import { supplementalFacilityAnswers } from './state-supplemental-answers.ts';
import type { FacilityMeta } from './tier2submit-export.ts';
import type { TierIIReportFormEntry } from '../types/reporting.ts';
import { casIsValid } from '../../tri-shared/services/cas.ts';
import { EHS_QUALIFIERS } from './ehs-qualifiers.ts';
import { facilityGeographyIssues, tier2SubmitEhsCas } from './validation-reference.ts';
import { carrierRequested } from './state-supplemental-fields.ts';
import { STATE_FIELDS_YEAR } from './state-fields.ts';
import { isValidPhone } from './phone.ts';

export function parentCompanyPhoneValidation(meta: FacilityMeta): ReportValidation {
  const errors: string[] = [];
  const warnings: string[] = [];
  const phone = supplementalFacilityAnswers(meta.stateReporting, meta.state).parentCompanyPhone;
  const parents = meta.contacts.filter((contact) => contact.contactTypes.includes('Parent Company'));
  if (parents.some((contact) => !contact.phones.length))
    warnings.push('A phone number is requested for each parent-company contact.');
  if (phone !== null && phone !== '') {
    if (!isValidPhone(phone)) errors.push('Parent company phone must be a valid phone number.');
    if (!parents.length)
      errors.push('Add the Parent Company name in Facility Report Information before supplying its phone.');
  }
  return { errors, warnings };
}

export function facilityMailingCountry(meta: FacilityMeta): string {
  return supplementalFacilityAnswers(meta.stateReporting, meta.state).mailingCountry?.trim() ?? '';
}
export function facilityExtraValidation(meta: FacilityMeta): ReportValidation {
  const errors = facilityGeographyIssues(meta);
  if (meta.documentStatus.fileCount === 0 && (meta.documentStatus.kinds.length > 0 || meta.documentStatus.sdsComplete))
    errors.push('Report attachment coverage is inconsistent: no files are included. Refresh the report documents.');
  const warnings: string[] = [];
  if (meta.zip.trim() && !zipValid(meta.zip))
    (meta.state === 'RI' ? errors : warnings).push('Facility ZIP code should be five digits or ZIP+4.');
  const mailing = facilityMailingValidation(meta);
  errors.push(...mailing.errors);
  warnings.push(...mailing.warnings);
  for (const contact of meta.contacts) {
    if (!contact.mailingAddress) continue;
    const result = addressValidation(
      contact.mailingAddress,
      (contact.name.trim() || contact.contactTypes.join(' / ') || 'Contact') + ' mailing address',
      false,
    );
    errors.push(...result.errors);
    warnings.push(...result.warnings);
  }
  if (carrierRequested(meta.state, meta.reportingYear) && !meta.stateReporting.chemicalCarriers[meta.state]?.length)
    warnings.push(meta.state + ': a Chemical Carrier contact is requested.');
  const parentPhone = parentCompanyPhoneValidation(meta);
  errors.push(...parentPhone.errors);
  warnings.push(...parentPhone.warnings);
  if (
    meta.state === 'ME' &&
    meta.reportingYear === STATE_FIELDS_YEAR &&
    meta.contacts.some((contact) => contact.contactTypes.includes('Submitter') && !contact.email?.trim())
  )
    errors.push('Maine: a Submitter email is required.');
  return { errors: [...new Set(errors)], warnings: [...new Set(warnings)] };
}
/** Internal TRI categories have names but no reportable CAS number. */
export function reportCasValue(cas: string | null): string | null {
  return cas === null || /^N\d{3}$/.test(cas) || cas === '00-00-0' ? null : cas;
}
export function entryExtraValidation(
  meta: Pick<FacilityMeta, 'state' | 'reportingYear'>,
  entry: TierIIReportFormEntry,
): ReportValidation {
  const errors: string[] = [],
    warnings: string[] = [];
  const label = entry.basis === 'compound' ? entry.chemicalName : entry.productName;
  const validate = (raw: string | null, name: string, isEhs: boolean, component: boolean) => {
    const cas = reportCasValue(raw);
    if (cas && !casIsValid(cas)) errors.push(name + ': CAS number has invalid formatting or checksum.');
    if (isEhs && !cas) errors.push(name + ': an EHS CAS number is required.');
    if (meta.reportingYear === STATE_FIELDS_YEAR && cas && casIsValid(cas)) {
      if (isEhs && !tier2SubmitEhsCas.has(cas))
        errors.push(name + ': marked EHS but its CAS number is not on the Tier2 Submit EHS list.');
      if (!isEhs && tier2SubmitEhsCas.has(cas) && !(cas in EHS_QUALIFIERS))
        errors.push(name + ': its CAS number is on the Tier2 Submit EHS list; review the EHS classification.');
    }
    if (component && !name.trim() && (!cas || isEhs)) errors.push(label + ': component name is required.');
    if (component && !name.trim() && cas && !isEhs && meta.state === 'CO')
      warnings.push(label + ': Colorado requests a name for each non-EHS component.');
  };
  if (entry.basis === 'compound') validate(entry.casOrCategory, label, entry.isEHS, false);
  else {
    if (entry.primaryReportingCas && !casIsValid(entry.primaryReportingCas))
      errors.push(label + ': primary reporting CAS has invalid formatting or checksum.');
    for (const component of entry.ehsChemicals) validate(component.casOrCategory, component.name, true, true);
    for (const component of entry.nonEhsChemicals) validate(component.casOrCategory, component.name, false, true);
    if (entry.isEHS !== entry.ehsChemicals.length > 0)
      errors.push(label + ': the mixture EHS answer must match its EHS components.');
  }
  if (!entry.isConfidential && entry.vessels.some((vessel) => /confidential/i.test(vessel.storageLocation)))
    warnings.push(
      label +
        ': use the Confidential Location checkbox instead of entering confidentiality text as a storage location.',
    );
  return { errors, warnings };
}
export function tier2SubmitExportWarnings(meta: FacilityMeta, entries: TierIIReportFormEntry[]): string[] {
  return [
    ...new Set([
      ...facilityExtraValidation(meta).warnings,
      ...(meta.stateReporting.chemicalCarriers[meta.state] ?? []).flatMap(
        (carrier, index) =>
          addressValidation(carrier, chemicalCarrierLabel(carrier, index) + ' mailing address', false).warnings,
      ),
      ...entries.flatMap((entry) => entryExtraValidation(meta, entry).warnings),
    ]),
  ];
}

export function facilityMailingValidation(meta: FacilityMeta) {
  const required = meta.reportingYear === STATE_FIELDS_YEAR && ['ME', 'GA'].includes(meta.state);
  return addressValidation(
    {
      street: meta.mailingAddress,
      city: meta.mailingCity,
      state: meta.mailingState,
      zip: meta.mailingZip,
      country: facilityMailingCountry(meta),
    },
    'Facility mailing address',
    required,
  );
}
